import type { Cliente, Movimiento, Producto, Venta } from '../types'
import { fecha, hora, nombreCompleto } from '../lib/format'

/** Descarga un libro con una o varias hojas.
 *
 *  Los reportes se abren en Excel, no en la app, así que las columnas
 *  llevan encabezados en español y los números van como número —no como
 *  texto— para que las fórmulas del contador funcionen. */
export async function descargarLibro(
  hojas: { nombre: string; filas: Record<string, unknown>[] }[],
  archivo: string,
) {
  // SheetJS pesa cerca de 400 kB. Se carga al exportar, no al abrir la
  // app: la mayoría de las sesiones nunca descarga un Excel.
  const XLSX = await import('xlsx')
  const libro = XLSX.utils.book_new()

  for (const hoja of hojas) {
    const datos = hoja.filas.length ? hoja.filas : [{ 'Sin datos': '' }]
    const ws = XLSX.utils.json_to_sheet(datos)
    ws['!cols'] = Object.keys(datos[0]).map((clave) => ({
      wch: Math.min(
        42,
        Math.max(clave.length + 2, ...datos.map((f) => String(f[clave] ?? '').length + 2)),
      ),
    }))
    // Excel limita los nombres de hoja a 31 caracteres.
    XLSX.utils.book_append_sheet(libro, ws, hoja.nombre.slice(0, 31))
  }

  XLSX.writeFile(libro, `${archivo}-${new Date().toISOString().slice(0, 10)}.xlsx`)
}

export async function exportarVentasExcel(ventas: Venta[], archivo = 'ventas') {
  const resumen = ventas.map((v) => ({
    Folio: v.folio,
    Fecha: fecha(v.fecha),
    Hora: hora(v.fecha),
    Cliente: v.clienteNombre,
    Tipo: v.clienteTipo === 'empresa' ? 'Empresa' : 'Particular',
    Vendedor: v.usuarioNombre,
    Productos: v.lineas.length,
    Total: v.total,
    Costo: v.costoTotal,
    Utilidad: v.utilidad,
    'Método de pago': v.metodoPago,
    Estado: v.estado,
    Pagado: v.pagado ? 'Sí' : 'No',
    Observaciones: v.observaciones ?? '',
  }))

  // El detalle línea a línea es lo que permite cruzar por producto.
  const detalle = ventas.flatMap((v) =>
    v.lineas.map((l) => ({
      Folio: v.folio,
      Fecha: fecha(v.fecha),
      Cliente: v.clienteNombre,
      Vendedor: v.usuarioNombre,
      Código: l.codigo,
      Producto: l.nombre,
      Cantidad: l.cantidad,
      'Precio unitario': l.precioUnitario,
      'Costo unitario': l.costoUnitario,
      Subtotal: l.subtotal,
      Utilidad: (l.precioUnitario - l.costoUnitario) * l.cantidad,
    })),
  )

  await descargarLibro(
    [
      { nombre: 'Ventas', filas: resumen },
      { nombre: 'Detalle por producto', filas: detalle },
    ],
    archivo,
  )
}

export async function exportarInventarioExcel(productos: Producto[], umbralGlobal: number) {
  const filas = productos.map((p) => ({
    Código: p.codigo,
    Producto: p.nombre,
    Categoría: p.categoria,
    Contenido: p.peso,
    Unidad: p.unidad,
    Stock: p.stock,
    Reservado: p.reservado,
    Disponible: p.stock - p.reservado,
    'Aviso bajo': p.stockMinimo ?? umbralGlobal,
    'Precio costo': p.precioCosto,
    'Precio venta': p.precioSugerido,
    'Margen %': p.precioSugerido
      ? Math.round(((p.precioSugerido - p.precioCosto) / p.precioSugerido) * 100)
      : 0,
    'Valor stock costo': p.stock * p.precioCosto,
    'Valor stock venta': p.stock * p.precioSugerido,
    Estado: p.activo ? 'Activo' : 'Descontinuado',
    'Código de barras': p.codigoBarras ?? '',
  }))
  await descargarLibro([{ nombre: 'Inventario', filas }], 'inventario-novaberry')
}

export async function exportarClientesExcel(clientes: Cliente[], ventas: Venta[]) {
  const filas = clientes.map((c) => {
    const suyas = ventas.filter((v) => v.clienteId === c.id && v.estado !== 'anulada')
    const total = suyas.reduce((s, v) => s + v.total, 0)
    return {
      Nombre: nombreCompleto(c),
      Tipo: c.tipo === 'empresa' ? 'Empresa' : 'Particular',
      Rubro: c.rubro ?? '',
      RUT: c.rut ?? '',
      Teléfono: c.telefono ?? '',
      Correo: c.email ?? '',
      Dirección: c.direccion ?? '',
      Comuna: c.comuna ?? '',
      Contacto: c.contacto ?? '',
      Compras: suyas.length,
      'Total comprado': total,
      'Promedio por compra': suyas.length ? Math.round(total / suyas.length) : 0,
      'Última compra': suyas.length ? fecha(suyas[0].fecha) : '',
      Observaciones: c.observaciones ?? '',
    }
  })
  await descargarLibro([{ nombre: 'Clientes', filas }], 'clientes-novaberry')
}

export async function exportarMovimientosExcel(movimientos: Movimiento[], productos: Producto[]) {
  const nombres = new Map(productos.map((p) => [p.id, p]))
  const filas = movimientos.map((m) => ({
    Fecha: fecha(m.fecha),
    Hora: hora(m.fecha),
    Tipo: m.tipo,
    Código: nombres.get(m.productoId)?.codigo ?? '',
    Producto: nombres.get(m.productoId)?.nombre ?? m.productoId,
    Cantidad: m.cantidad,
    'Stock resultante': m.stockResultante,
    Proveedor: m.proveedor ?? '',
    Costo: m.costo ?? '',
    Usuario: m.usuarioNombre,
    Observaciones: m.observaciones ?? '',
  }))
  await descargarLibro([{ nombre: 'Movimientos', filas }], 'movimientos-novaberry')
}
